import React, { useState, useEffect, useCallback } from "react";
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';

import AddTaskModal from "../components/AddTaskModal";
import ScheduleTasksSkeleton from '../components/layout/ScheduleTasksSkeleton';
import api from '../api/api';
import { useTheme } from '../hooks/useTheme';

// Mobile gets the agenda-style month list (how phone calendar apps present
// schedules); desktop keeps the classic month grid. Tracked live so resizing
// or toggling device emulation switches the calendar accordingly.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

export default function ScheduleTasksPage() {
    const { isDarkMode } = useTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const isMobile = useIsMobile();
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTab, setActiveTab] = useState("All Tasks");
  
  const tabs = ["All Tasks", "Performance", "Skilling", "Networking", "Giving Back", "Feedback"];

  // Map focus areas to calendar color categories
  const getFocusAreaKind = (focusAreaName) => {
    if (!focusAreaName) return '';
    
    const areaLower = focusAreaName.toLowerCase();
    if (areaLower.includes('performance') || areaLower.includes('career')) return 'performance';
    if (areaLower.includes('skill') || areaLower.includes('learning')) return 'skilling';
    if (areaLower.includes('network') || areaLower.includes('social')) return 'networking';
    if (areaLower.includes('giving') || areaLower.includes('back')) return 'giving-back';
    if (areaLower.includes('feedback')) return 'feedback';
    return '';
  };

  // Fetch tasks from backend
  const fetchTasks = useCallback(async () => {
    // Transform backend data to FullCalendar format
    // Only show events on their due date
    const transformTasksToEvents = (tasks) => {
      return tasks.map(task => {
        // Use DueDate or EndDate as the event date (only show on that date)
        const eventDate = task.DueDate || task.EndDate || task.StartDate;
        if (!eventDate) return null;
        
        // Parse the date and set to start of day (all-day event)
        const date = new Date(eventDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        return {
          id: task.TaskID || task.Title + task.StartDate,
          title: task.Title,
          start: dateStr, // Only show on due date
          allDay: true, // Always all-day events
          extendedProps: {
            kind: getFocusAreaKind(task.FocusAreaName),
            description: task.Description,
            difficulty: task.DifficultyLevel,
            focusArea: task.FocusAreaName
          }
        };
      }).filter(event => event !== null); // Remove null entries
    };

    try {
      setLoading(true);
      const response = await api.get("/task/activetasks");
      
      if (Array.isArray(response.data)) {
        const calendarEvents = transformTasksToEvents(response.data);
        setEvents(calendarEvents);
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch tasks on mount
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Filter events by active tab
  const filteredEvents = React.useMemo(() => {
    if (activeTab === "All Tasks") return events;
    
    const tabToKind = {
      "Performance": "performance",
      "Skilling": "skilling",
      "Networking": "networking",
      "Giving Back": "giving-back",
      "Feedback": "feedback"
    };
    
    const kind = tabToKind[activeTab];
    return events.filter(event => event.extendedProps?.kind === kind);
  }, [events, activeTab]);

  // Handle task creation success
  const handleTaskCreated = (newTask) => {
    console.log('New task created:', newTask);
    // Refresh the calendar by fetching tasks again
    fetchTasks();
  };

  // Handle date/time selection on calendar
  const handleDateSelect = (selectInfo) => {
    setSelectedDate(selectInfo.startStr);
    setShowAddTaskModal(true);
  };

  // Handle event click
  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    const props = event.extendedProps;
    setViewingTask({
      title: event.title,
      description: props.description || '',
      difficulty: props.difficulty || '',
      focusArea: props.focusArea || '',
      date: event.start ? event.start.toLocaleDateString() : '',
    });
  };

  // Calculate statistics
  const stats = React.useMemo(() => {
    const now = new Date();
    const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
    const weekEnd = new Date(now.setDate(now.getDate() + 6));
    
    const thisWeekTasks = events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate >= weekStart && eventDate <= weekEnd;
    });

    const completed = events.filter(e => e.extendedProps?.completed).length;
    const inProgress = events.filter(e => !e.extendedProps?.completed && new Date(e.start) <= new Date()).length;
    
    const skillingCount = events.filter(e => e.extendedProps?.kind === 'skilling').length;
    const skillingPct = events.length > 0 ? Math.round((skillingCount / events.length) * 100) : 0;

    return {
      thisWeek: thisWeekTasks.length,
      completed,
      inProgress,
      focusSkillingPct: skillingPct
    };
  }, [events]);

  if (loading) {
    return <ScheduleTasksSkeleton isDarkMode={isDarkMode} />;
  }

  return (
    <>
      <div className={`min-h-screen ${isDarkMode ? 'bg-surface text-slate-100' : 'bg-surface text-slate-900'}`}>
        {/* Header Section */}
        <section className="max-w-6xl mx-auto px-4 py-6 md:py-8">
          <header className="flex items-start md:items-center justify-between gap-4 mb-6">
            <div className="min-w-0">
              <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Schedule Tasks</h1>
              <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>View and manage your tasks in a calendar view</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => setShowAddTaskModal(true)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors flex items-center gap-2 ${
                  isDarkMode
                    ? 'bg-primary-700 hover:bg-primary-800 text-white'
                    : 'bg-primary-700 hover:bg-primary-800 text-white'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Task
              </button>
            </div>
          </header>

          {/* Tabs */}
          <div className={`sticky top-0 z-10 mb-6 backdrop-blur ${
            isDarkMode ? 'bg-slate-900/80 supports-[backdrop-filter]:bg-slate-900/60' : 'bg-white/80 supports-[backdrop-filter]:bg-white/60'
          }`}>
            <div className="flex flex-wrap items-center gap-2 p-2 pl-0">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? "bg-primary-700 text-white border-primary-700"
                      : isDarkMode
                        ? "bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
        </section>

        <main className="max-w-6xl mx-auto px-4 pb-8">
          {/* Calendar */}
          <section className="mb-6">
            <div className={`rounded-xl border p-6 shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
              <style>{`
                .fc {
                  font-family: system-ui, -apple-system, sans-serif;
                  ${isDarkMode ? 'color: #e2e8f0;' : 'color: #1e293b;'}
                }
                .fc-header-toolbar {
                  margin-bottom: 1.5rem;
                  padding-bottom: 1rem;
                  border-bottom: 1px solid ${isDarkMode ? '#475569' : '#e5e7eb'};
                }
                .fc-toolbar-title {
                  ${isDarkMode ? 'color: #f1f5f9;' : 'color: #0f172a;'}
                  font-weight: 700;
                  font-size: 1.5rem;
                }
                .fc-button {
                  background-color: ${isDarkMode ? '#1e293b' : '#ffffff'} !important;
                  border-color: ${isDarkMode ? '#475569' : '#e5e7eb'} !important;
                  color: ${isDarkMode ? '#cbd5e1' : '#475569'} !important;
                  padding: 0.5rem 1rem;
                  border-radius: 0.5rem;
                  font-weight: 500;
                  transition: all 0.2s;
                }
                .fc-button:hover {
                  background-color: ${isDarkMode ? '#334155' : '#f8fafc'} !important;
                  border-color: ${isDarkMode ? '#64748b' : '#cbd5e1'} !important;
                  color: ${isDarkMode ? '#e2e8f0' : '#334155'} !important;
                }
                .fc-button-active {
                  background-color: #5A7CDE !important;
                  border-color: #5A7CDE !important;
                  color: white !important;
                }
                .fc-button-active:hover {
                  background-color: #4A66B6 !important;
                  border-color: #4A66B6 !important;
                  color: white !important;
                }
                .fc-prev-button, .fc-next-button {
                  background-color: ${isDarkMode ? '#1e293b' : '#ffffff'} !important;
                  border-color: ${isDarkMode ? '#475569' : '#e5e7eb'} !important;
                  color: ${isDarkMode ? '#cbd5e1' : '#475569'} !important;
                }
                .fc-prev-button:hover, .fc-next-button:hover {
                  background-color: ${isDarkMode ? '#334155' : '#f8fafc'} !important;
                  color: ${isDarkMode ? '#e2e8f0' : '#334155'} !important;
                }
                .fc-today-button {
                  background-color: ${isDarkMode ? '#1e293b' : '#ffffff'} !important;
                  border-color: ${isDarkMode ? '#475569' : '#e5e7eb'} !important;
                  color: ${isDarkMode ? '#cbd5e1' : '#475569'} !important;
                }
                .fc-today-button:hover {
                  background-color: ${isDarkMode ? '#334155' : '#f8fafc'} !important;
                  color: ${isDarkMode ? '#e2e8f0' : '#334155'} !important;
                }
                .fc-prev-button .fc-icon, .fc-next-button .fc-icon {
                  color: ${isDarkMode ? '#cbd5e1' : '#475569'} !important;
                }
                .fc-prev-button:hover .fc-icon, .fc-next-button:hover .fc-icon {
                  color: ${isDarkMode ? '#e2e8f0' : '#334155'} !important;
                }
                .fc-daygrid-day {
                  border-color: ${isDarkMode ? '#475569' : '#e5e7eb'};
                }
                .fc-day-today {
                  background-color: ${isDarkMode ? 'rgba(90, 124, 222, 0.16) !important' : '#EFF2FC !important'};
                  border-color: ${isDarkMode ? '#5A7CDE' : '#5A7CDE'} !important;
                }
                .fc-day-today .fc-daygrid-day-number {
                  ${isDarkMode ? 'color: #5A7CDE !important; font-weight: 700;' : 'color: #4A66B6 !important; font-weight: 700;'}
                }
                .fc-col-header-cell {
                  ${isDarkMode ? 'background-color: #0f172a; color: #cbd5e1; border-color: #475569;' : 'background-color: #f8fafc; color: #475569; border-color: #e5e7eb;'}
                  padding: 0.75rem 0.5rem;
                  font-weight: 600;
                  font-size: 0.875rem;
                  text-transform: uppercase;
                  letter-spacing: 0.05em;
                }
                .fc-daygrid-day-frame {
                  ${isDarkMode ? 'background-color: #1e293b;' : 'background-color: #ffffff;'}
                  padding: 0.5rem;
                }
                .fc-daygrid-day-number {
                  ${isDarkMode ? 'color: #cbd5e1;' : 'color: #475569;'}
                  font-weight: 500;
                  padding: 0.5rem;
                }
                .fc-daygrid-day:hover .fc-daygrid-day-frame {
                  ${isDarkMode ? 'background-color: #334155;' : 'background-color: #f8fafc;'}
                }
                .fc-daygrid-day.fc-day-other .fc-daygrid-day-frame {
                  ${isDarkMode ? 'background-color: #0f172a; opacity: 0.5;' : 'background-color: #f8fafc; opacity: 0.6;'}
                }
                .fc-event {
                  border-radius: 0.5rem;
                  padding: 0.375rem 0.625rem;
                  cursor: pointer;
                  border: none;
                  font-weight: 500;
                  font-size: 0.875rem;
                  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                  transition: all 0.2s;
                }
                .fc-event:hover {
                  transform: translateY(-1px);
                  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }
                .evt-skilling {
                  background-color: #10b981;
                  color: white;
                }
                .evt-performance {
                  background-color: #f59e0b;
                  color: white;
                }
                .evt-networking {
                  background-color: #3b82f6;
                  color: white;
                }
                .evt-giving-back {
                  background-color: #8b5cf6;
                  color: white;
                }
                .evt-feedback {
                  background-color: #ef4444;
                  color: white;
                }
                .fc-more-link {
                  ${isDarkMode ? 'color: #cbd5e1;' : 'color: #475569;'}
                  font-weight: 500;
                }
                .fc-daygrid-more-link {
                  ${isDarkMode ? 'background-color: #334155;' : 'background-color: #f1f5f9;'}
                  border-radius: 0.375rem;
                  padding: 0.25rem 0.5rem;
                }
                @media (max-width: 767px) {
                  .fc-toolbar-title { font-size: 1.125rem !important; }
                  .fc-header-toolbar { margin-bottom: 1rem; padding-bottom: 0.75rem; }
                  .fc .fc-daygrid-body, .fc .fc-scrollgrid-sync-table, .fc .fc-scroller { height: auto !important; }
                  .fc .fc-daygrid-day-frame { min-height: 3.2rem; }
                  .fc table { font-size: 0.85rem; }
                }
              `}</style>
              <FullCalendar
                key={isMobile ? 'mobile' : 'desktop'}
                plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                initialView={isMobile ? 'listMonth' : 'dayGridMonth'}
                headerToolbar={isMobile ? {
                  left: 'prev,next',
                  center: 'title',
                  right: 'today'
                } : {
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                height="auto"
                dayMaxEvents={true}
                nowIndicator={true}
                editable={true}
                selectable={true}
                events={filteredEvents}
                select={handleDateSelect}
                eventClick={handleEventClick}
                eventClassNames={(arg) => {
                  const kind = arg.event.extendedProps?.kind;
                  if (kind === 'skilling') return ['evt-skilling'];
                  if (kind === 'performance') return ['evt-performance'];
                  if (kind === 'networking') return ['evt-networking'];
                  if (kind === 'giving-back') return ['evt-giving-back'];
                  if (kind === 'feedback') return ['evt-feedback'];
                  return [];
                }}
                slotLabelFormat={{ hour: '2-digit', minute: '2-digit' }}
                titleFormat={{ year: 'numeric', month: 'long' }}
                dayHeaderFormat={{ weekday: 'short' }}
              />
            </div>
          </section>

          {/* Weekly Overview */}
          <section className="mb-6">
            <div className={`rounded-xl px-6 py-6 ${
              isDarkMode ? 'bg-slate-800 text-slate-200' : 'bg-primary-900 text-primary-50'
            }`}>
              <h3 className={`text-sm font-semibold mb-4 opacity-90 ${isDarkMode ? 'text-slate-300' : ''}`}>Weekly Overview</h3>
              <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
                <div className={`rounded-xl px-4 py-5 ${
                  isDarkMode ? 'bg-slate-700/40' : 'bg-primary-800/40'
                }`}>
                  <div className="text-xs uppercase tracking-wide opacity-80">Tasks This Week</div>
                  <div className="mt-2 text-2xl font-bold">{stats.thisWeek}</div>
                </div>
                <div className={`rounded-xl px-4 py-5 ${
                  isDarkMode ? 'bg-slate-700/40' : 'bg-primary-800/40'
                }`}>
                  <div className="text-xs uppercase tracking-wide opacity-80">Completed</div>
                  <div className="mt-2 text-2xl font-bold">{stats.completed}</div>
                </div>
                <div className={`rounded-xl px-4 py-5 ${
                  isDarkMode ? 'bg-slate-700/40' : 'bg-primary-800/40'
                }`}>
                  <div className="text-xs uppercase tracking-wide opacity-80">In Progress</div>
                  <div className="mt-2 text-2xl font-bold">{stats.inProgress}</div>
                </div>
                <div className={`rounded-xl px-4 py-5 ${
                  isDarkMode ? 'bg-slate-700/40' : 'bg-primary-800/40'
                }`}>
                  <div className="text-xs uppercase tracking-wide opacity-80">Focus: Skilling</div>
                  <div className="mt-2 text-2xl font-bold">{stats.focusSkillingPct}%</div>
                </div>
              </div>
            </div>
          </section>

          {/* Legend */}
          <section>
            <div className={`rounded-xl border p-4 shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
              <div className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Quick Guide:</div>
              <div className={`flex flex-wrap items-center gap-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                <div className="flex items-center gap-2">
                  <span className={`inline-block h-3 w-3 rounded ${isDarkMode ? 'bg-primary-500' : 'bg-primary-500'}`}></span>
                  <span>Skilling Tasks</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-block h-3 w-3 rounded ${isDarkMode ? 'bg-amber-500' : 'bg-amber-500'}`}></span>
                  <span>Performance Tasks</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-block h-3 w-3 rounded ${isDarkMode ? 'bg-blue-500' : 'bg-blue-500'}`}></span>
                  <span>Networking Tasks</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-block h-3 w-3 rounded ${isDarkMode ? 'bg-purple-500' : 'bg-purple-500'}`}></span>
                  <span>Giving Back</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-block h-3 w-3 rounded ${isDarkMode ? 'bg-red-500' : 'bg-red-500'}`}></span>
                  <span>Feedback</span>
                </div>
                <span className={isDarkMode ? 'text-slate-500' : 'text-slate-400'}>•</span>
                <span>Click on a date to add a task • Click on an event to view details</span>
              </div>
            </div>
          </section>
        </main>

        {/* Floating Add Button */}
        <button
          type="button"
          aria-label="add task"
          onClick={() => setShowAddTaskModal(true)}
          className={`fixed bottom-28 right-4 md:bottom-6 md:right-6 grid h-12 w-12 place-items-center rounded-full shadow-lg z-40 transition-colors ${
            isDarkMode 
              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' 
              : 'bg-amber-300 text-amber-900 hover:bg-amber-400'
          }`}
        >
          <span className="text-2xl leading-none">＋</span>
        </button>
      </div>

      {/* Task Detail Modal */}
      {viewingTask && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setViewingTask(null)}
        >
          <div
            className={`w-full max-w-md rounded-2xl border shadow-2xl p-6 ${
              isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <h2 className={`text-lg font-bold leading-snug ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                {viewingTask.title}
              </h2>
              <button
                type="button"
                onClick={() => setViewingTask(null)}
                aria-label="close task details"
                className={`shrink-0 rounded p-1 transition-colors ${
                  isDarkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                ✕
              </button>
            </div>
            {viewingTask.description && (
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                {viewingTask.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {viewingTask.focusArea && (
                <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full ${
                  isDarkMode ? 'bg-primary-900/50 text-primary-300' : 'bg-primary-50 text-primary-700'
                }`}>
                  {viewingTask.focusArea}
                </span>
              )}
              {viewingTask.difficulty && (
                <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full ${
                  isDarkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700'
                }`}>
                  {viewingTask.difficulty}
                </span>
              )}
              {viewingTask.date && (
                <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${
                  isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                }`}>
                  Due {viewingTask.date}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddTaskModal}
        onClose={() => {
          setShowAddTaskModal(false);
          setSelectedDate(null);
        }}
        onTaskCreated={handleTaskCreated}
        initialDate={selectedDate}
      />
    </>
  );
}
